// useCustomFieldsScreen.js — estado e chamadas da tela de perguntas. A página só entrega { vm, actions } à View do CD.
import { useMemo, useState } from 'react';
import { useCustomFields } from '../../hooks/queries/useCustomFields';
import { useEntities } from '../../hooks/queries';
import { useEntitlements, useLimit } from '../../hooks/useEntitlements';
import { useGuardedAction } from '../../hooks/useGuardedAction';
import useSnackbar from '../../hooks/useSnackbar';
import { useThemeLabels } from '../../hooks/useThemeLabels';
import { isPlansSaleEnabled } from '../../utils/environment';
import {
  buildCreatePayload, buildCustomFieldsVm, changeFieldType, draftFromPreset, newDraft, setFilterMode, toggleFilterId,
} from './customFieldsVmBuilder';

const LOAD_ERROR = 'Não foi possível carregar as perguntas. Tente novamente.';
const messageOf = (error) => error?.response?.data?.message || 'Não foi possível salvar. Tente novamente.';

export function useCustomFieldsScreen() {
  const data = useCustomFields();
  const entities = useEntities();
  const { planName } = useEntitlements();
  const { limit, current } = useLimit('custom_fields');
  const { themeInfo } = useThemeLabels();
  const guarded = useGuardedAction('custom_fields');
  const plansSaleEnabled = isPlansSaleEnabled();
  const { snackbar, showError, hideSnackbar } = useSnackbar();

  const [draft, setDraft] = useState(null);
  const [rename, setRename] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [stopBasic, setStopBasic] = useState(null);

  const patchDraft = (patch) => setDraft((d) => (d ? { ...d, ...patch } : d));

  // Sem venda de planos não existe limite: o guarda abriria a oferta de um plano que ninguém pode comprar.
  const openCreator = (nextDraft) => {
    const open = () => setDraft(nextDraft);
    if (plansSaleEnabled) guarded(open);
    else open();
  };

  const save = async () => {
    patchDraft({ saving: true, errorMessage: null, labelError: null });
    try {
      const payload = buildCreatePayload(draft);
      if (draft.systemKey) await data.createSystem({ systemKey: draft.systemKey, payload });
      else await data.create(payload);
      setDraft(null);
    } catch (error) {
      const message = messageOf(error);
      const onLabel = error?.response?.data?.field === 'label';
      patchDraft({ saving: false, labelError: onLabel ? message : null, errorMessage: onLabel ? null : message });
    }
  };

  const saveRename = async () => {
    setRename((r) => ({ ...r, saving: true, error: null }));
    try {
      await data.rename({ id: rename.id, label: rename.value });
      setRename(null);
    } catch (error) {
      setRename((r) => ({ ...r, saving: false, error: messageOf(error) }));
    }
  };

  const askDelete = (id) => {
    const field = data.fields.find((f) => f.id === id);
    if (!field) return;
    setConfirmation({
      id,
      title: `Apagar "${field.label}"?`,
      message: 'Ela some do formulário. As respostas já dadas continuam nos agendamentos.',
      confirmLabel: 'Apagar',
      deleting: false,
    });
  };

  // A View só desenha a confirmação dentro do cartão de pergunta, e dado básico não tem cartão: quem pergunta é a página.
  const askStopBasic = (id) => {
    const field = data.fields.find((f) => f.id === id);
    if (!field) return;
    setStopBasic({
      id,
      title: `Parar de pedir ${field.label.toLowerCase()}?`,
      message: 'Sai do formulário. Os dados já guardados nas fichas continuam lá.',
      confirmLabel: 'Parar de pedir',
    });
  };

  const confirmDelete = async () => {
    setConfirmation((c) => ({ ...c, deleting: true }));
    try {
      await data.remove(confirmation.id);
    } catch (error) {
      showError(messageOf(error));
    } finally {
      setConfirmation(null);
    }
  };

  const confirmStopBasic = async () => {
    const { id } = stopBasic;
    setStopBasic(null);
    try {
      await data.remove(id);
    } catch (error) {
      showError(messageOf(error));
    }
  };

  // Ativar pode bater no limite do plano, e reordenar volta ao lugar se falhar: nos dois casos, o aviso diz por quê.
  const toggleActive = async (id) => {
    const field = data.fields.find((f) => f.id === id);
    if (!field) return;
    try {
      await data.setActive({ id, isActive: !field.isActive });
    } catch (error) {
      showError(messageOf(error));
    }
  };

  const reorder = async (orderedIds) => {
    try {
      await data.reorder(orderedIds);
    } catch (error) {
      showError(messageOf(error));
    }
  };

  const vm = useMemo(() => buildCustomFieldsVm({
    loading: data.loading || entities.loading,
    errorMessage: data.error ? LOAD_ERROR : null,
    fields: data.fields,
    services: entities.services,
    professionals: entities.professionals,
    places: entities.places,
    limit,
    used: current,
    planName,
    plansSaleEnabled,
    themeId: themeInfo?.themeId || 'default',
    draft,
    rename,
    confirmation,
  }), [
    data.loading, data.error, data.fields, entities.loading, entities.services, entities.professionals, entities.places,
    limit, current, planName, plansSaleEnabled, themeInfo?.themeId, draft, rename, confirmation,
  ]);

  const actions = {
    onRetry: () => data.reload(),
    onNew: () => openCreator(newDraft()),
    onUseExample: (label) => openCreator(newDraft({ label })),
    onUseSuggestion: (key) => openCreator(draftFromPreset(themeInfo?.themeId || 'default', key)),
    onUseBasic: (systemKey) => setDraft(newDraft({ systemKey })), // dado básico não conta no limite
    onStopBasic: askStopBasic,
    onCloseCreator: () => setDraft(null),
    onCreatorChange: (patch) => setDraft((d) => (patch.fieldType ? changeFieldType(d, patch.fieldType) : { ...d, ...patch })),
    onRuleChange: (key, value) => setDraft((d) => ({ ...d, rules: { ...d.rules, [key]: value } })),
    onFilterModeChange: (key, mode) => setDraft((d) => setFilterMode(d, key, mode)),
    onFilterToggle: (key, id) => setDraft((d) => toggleFilterId(d, key, id)),
    onSave: save,
    onRename: (id) => {
      const field = data.fields.find((f) => f.id === id);
      if (field) setRename({ id, value: field.label, error: null, saving: false });
    },
    onRenameChange: (value) => setRename((r) => ({ ...r, value })),
    onRenameSave: saveRename,
    onRenameCancel: () => setRename(null),
    onToggleActive: toggleActive,
    onReorder: reorder,
    onDelete: askDelete,
    onConfirmDelete: confirmDelete,
    onCancelDelete: () => setConfirmation(null),
  };

  return {
    vm,
    actions,
    snackbar,
    hideSnackbar,
    stopBasic,
    onConfirmStopBasic: confirmStopBasic,
    onCancelStopBasic: () => setStopBasic(null),
  };
}
